/**
 * Release schedule — the runtime, date-aware release overrides an admin sets in
 * the Dex Config portal (GET /api/dex-config → `releaseSchedule`), applied over
 * the client's bundled availability rosters.
 *
 * WHY a date. Before this, the dex-config editor could only force a species
 * released/unreleased *now*, and only on the base axis. Two things were missing:
 *   1. a way to SCHEDULE availability ("Solgaleo's shiny drops on <date>"), and
 *   2. a per-variant axis — shinies release much later than the base Pokémon, so
 *      shiny needs its own override + date, separate from the base release.
 *
 * This module holds `{ variant → { add: {dexId, releaseAt}[], remove: dexId[] } }`
 * and evaluates `releaseAt` at READ time: an add is active when `releaseAt` is
 * null (immediate) or has passed. So a future date auto-flips to available the
 * moment the app reads availability after that time — no cron, no refetch. The
 * base ('pokemon') schedule cascades to lucky/perfect/xxs/xxl and shiny cascades
 * to shundo/shinyLucky via variantAvailability's effective sets.
 *
 * The `remove` list (forced-unreleased) is always immediate — you don't schedule
 * a removal, you just apply it.
 */
import type { PokedexVariant } from "./pogoPokedexVariantClassifier";

/** A dex id with an optional release date (epoch-ms; null = effective now). */
export interface ScheduledDexId {
  dexId: number;
  releaseAt: number | null;
}

export interface VariantReleaseSchedule {
  released: ScheduledDexId[];
  unreleased: number[];
}

/** Server wire shape (keyed by PokedexVariant string). */
export type ReleaseScheduleWire = Record<string, VariantReleaseSchedule>;

interface CompiledVariant {
  /** dexId → releaseAt (null = immediate). */
  add: Map<number, number | null>;
  remove: Set<number>;
}

const compiled = new Map<string, CompiledVariant>();
let scheduleVersion = 0;

/** Monotonic version, bumped on every replace — lets consumers cache-key. */
export function releaseScheduleVersion(): number {
  return scheduleVersion;
}

/**
 * Replace the entire runtime release schedule (called by the dex-config syncer
 * with the server payload). Passing an empty object clears all overrides.
 */
export function setReleaseSchedule(wire: ReleaseScheduleWire | null | undefined): void {
  compiled.clear();
  if (wire) {
    for (const [variant, bucket] of Object.entries(wire)) {
      const add = new Map<number, number | null>();
      for (const e of bucket?.released ?? []) {
        if (Number.isFinite(e?.dexId) && e.dexId > 0) {
          add.set(e.dexId, typeof e.releaseAt === "number" ? e.releaseAt : null);
        }
      }
      const remove = new Set<number>();
      for (const id of bucket?.unreleased ?? []) {
        if (Number.isFinite(id) && id > 0) remove.add(id);
      }
      if (add.size > 0 || remove.size > 0) compiled.set(variant, { add, remove });
    }
  }
  scheduleVersion += 1;
}

/** True when the variant has no overrides at all (the identity fast path). */
export function hasNoSchedule(variant: PokedexVariant | string): boolean {
  return !compiled.has(variant);
}

/**
 * The forced-released ids for a variant that are ACTIVE right now (releaseAt
 * null or already passed). Evaluated against `Date.now()` at call time — this is
 * the read-time auto-flip.
 */
export function activeAddSet(variant: PokedexVariant | string): ReadonlySet<number> {
  const v = compiled.get(variant);
  if (!v || v.add.size === 0) return EMPTY;
  const now = Date.now();
  const out = new Set<number>();
  for (const [id, at] of v.add) {
    if (at == null || at <= now) out.add(id);
  }
  return out;
}

/** The forced-unreleased ids for a variant (always immediate). */
export function removeSet(variant: PokedexVariant | string): ReadonlySet<number> {
  const v = compiled.get(variant);
  return v && v.remove.size > 0 ? v.remove : EMPTY;
}

/**
 * True when the variant has at least one add whose `releaseAt` is still in the
 * future. Consumers that cache an effective set must bypass the cache while this
 * holds, so the set re-evaluates once the date passes.
 */
export function hasFutureAdds(variant: PokedexVariant | string): boolean {
  const v = compiled.get(variant);
  if (!v || v.add.size === 0) return false;
  const now = Date.now();
  for (const at of v.add.values()) {
    if (at != null && at > now) return true;
  }
  return false;
}


const EMPTY: ReadonlySet<number> = new Set<number>();
